import { CheckCircleIcon, MinusCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";

import { cn } from "../../utls/utils";
import Typography from "../typography";

type BooleanState = boolean | null | undefined;

type BooleanIconSize = "sm" | "md" | "lg";

export interface BooleanIconProps {
  /** Value to render. `true` shows a check, `false` a cross, `null/undefined` a neutral icon. */
  value: BooleanState;
  /** Whether to render the associated label next to the icon. */
  showLabel?: boolean;
  /** Label used when the value is truthy. */
  trueLabel?: string;
  /** Label used when the value is falsy. */
  falseLabel?: string;
  /** Label used when the value is unknown. */
  neutralLabel?: string;
  /** Control the icon size. */
  size?: BooleanIconSize;
  /** Additional className for the wrapper. */
  className?: string;
}

const sizeClasses: Record<BooleanIconSize, string> = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
};

function resolveState(value: BooleanState) {
  if (value === true) {
    return {
      Icon: CheckCircleIcon,
      label: "Yes",
      color: "text-emerald-600",
    } as const;
  }

  if (value === false) {
    return {
      Icon: XCircleIcon,
      label: "No",
      color: "text-danger",
    } as const;
  }

  return {
    Icon: MinusCircleIcon,
    label: "Unknown",
    color: "text-brown-400",
  } as const;
}

export function BooleanIcon({
  value,
  showLabel = false,
  trueLabel = "Yes",
  falseLabel = "No",
  neutralLabel = "Unknown",
  size = "md",
  className,
}: BooleanIconProps) {
  const { Icon, color } = resolveState(value);
  const resolvedLabel =
    value === true ? trueLabel : value === false ? falseLabel : neutralLabel;

  return (
    <span className={cn("inline-flex items-center gap-2", className)} aria-live="polite">
      <Icon
        aria-hidden
        data-testid="boolean-icon"
        className={cn("flex-shrink-0", sizeClasses[size], color)}
      />
      <span className="sr-only">{resolvedLabel}</span>
      {showLabel ? (
        <Typography variant="body2" className="text-brown-700">
          {resolvedLabel}
        </Typography>
      ) : null}
    </span>
  );
}

export default BooleanIcon;
